import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck } from "lucide-react";
import client, { extractError } from "@/api";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";

export default function AuthPage({ onAuth }) {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login" ? { email, password } : { name, email, password };
      const result = await client.post(endpoint, payload);
      onAuth(result.data);
    } catch (err) {
      setError(extractError(err, "We could not sign you in. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const switchMode = (target) => {
    setMode(target);
    setError("");
  };

  return (
    <main className="auth-page">
      <Seo
        title="Log in · Barter India"
        description="Log in to your Barter India account to manage your company profile, subscription, and trade activity."
        path="/login"
        robots="noindex,follow"
      />
      <section className="auth-intro">
        <div className="brand-lockup" data-testid="brand-lockup"><span className="brand-mark">B</span><span>Barter India</span></div>
        <div className="intro-copy">
          <p className="eyebrow">BUSINESS NETWORK / PHASE 02</p>
          <h1>Trade more.<br /><em>Pay less.</em><br />Grow together.</h1>
          <p>Register your company, complete verification, and unlock the barter marketplace when it opens for trade.</p>
        </div>
        <div className="intro-footer"><span>Trusted foundations for ambitious businesses</span><span className="status-dot">● Verification live</span></div>
      </section>
      <section className="auth-panel">
        <div className="auth-form-wrap">
          <div className="auth-mode-switch" data-testid="auth-mode-switch">
            <button type="button" data-testid="mode-login" className={mode === "login" ? "active" : ""} onClick={() => switchMode("login")}>Sign in</button>
            <button type="button" data-testid="mode-register" className={mode === "register" ? "active" : ""} onClick={() => switchMode("register")}>Register company admin</button>
          </div>
          <h2>{mode === "login" ? "Welcome back" : "Create your workspace"}</h2>
          <p className="muted">{mode === "login" ? "Sign in to your Barter India workspace." : "Create the administrator account. You will register the company itself after signing in."}</p>
          <form onSubmit={submit} data-testid={mode === "login" ? "login-form" : "register-form"}>
            {mode === "register" && (
              <>
                <label htmlFor="name">Your name</label>
                <input id="name" data-testid="register-name-input" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ravi Verma" required minLength={2} />
              </>
            )}
            <label htmlFor="email">Work email</label>
            <input id="email" data-testid={`${mode}-email-input`} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required />
            <label htmlFor="password">Password</label>
            <input id="password" data-testid={`${mode}-password-input`} type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={mode === "login" ? "Enter your password" : "Minimum 8 characters"} required minLength={8} />
            {error && <div className="form-error" data-testid={`${mode}-error`}>{error}</div>}
            <button className="primary-button" data-testid={`${mode}-submit-button`} type="submit" disabled={busy}>
              {busy ? "Working…" : mode === "login" ? "Sign in" : "Create account"}
              <ArrowUpRight size={17} />
            </button>
          </form>
          {mode === "login" && (
            <p className="form-note" data-testid="forgot-password-note">
              Forgot your password?{" "}
              <Link to="/forgot-password" data-testid="forgot-password-link">
                Reset it here
              </Link>
            </p>
          )}
          <p className="form-note" data-testid="security-note">
            <ShieldCheck size={15} /> Protected with role-based access controls
          </p>
        </div>
      </section>
      <Footer />
    </main>
  );
}
